/**
 * Per-question human evaluation layer.
 *
 * Renders:
 *   • progress bar (X of Y completed)
 *   • question-type legend
 *   • pager (1..N) to jump between questions
 *   • per-question card with 5 category rows (1–5 buttons)
 *   • optional note textarea + Keep / Improve / Replace recommendation
 *   • autosave per change (debounced 700ms)
 *
 * Data is loaded via GET /api/evaluations/:id/questions and saved via
 * PUT /api/evaluations/:id/questions/:index.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { AlertCircle, CheckCircle, Clock, Loader2, Lock } from 'lucide-react'
import { evaluationsApi } from '../../lib/api/evaluations'

// ── Types ────────────────────────────────────────────────────────────────────

interface CategoryItem {
  id: number
  key: string
  label: string
  description: string
  display_order: number
}

interface QuestionResponse {
  id: number
  evaluation_id: number
  question_index: number
  scores: Record<string, number>
  note: string | null
  recommendation: string | null
  completion_status: 'not_started' | 'in_progress' | 'completed'
}

interface QuestionItem {
  question_index: number
  question_text: string
  expected_answer: string | null
  question_type_id: number | null
  question_type_slug: string | null
  question_type_name: string | null
  categories: CategoryItem[]
  response: QuestionResponse | null
}

interface Bundle {
  evaluation_id: number
  total: number
  completed: number
  in_progress: number
  items: QuestionItem[]
}

// ── Visual tokens ────────────────────────────────────────────────────────────

const TYPE_COLORS: Record<string, string> = {
  behavioral: '#4f46e5',
  situational: '#0891b2',
  tell_me_about_yourself: '#7c3aed',
  motivation_fit: '#d97706',
  role_specific: '#059669',
  resume_based: '#db2777',
}

const RATING_LABELS = ['Very Weak', 'Weak', 'Mixed', 'Strong', 'Very Strong']
const RECOMMENDATIONS: { value: string; label: string }[] = [
  { value: '', label: 'No recommendation' },
  { value: 'keep', label: 'Keep' },
  { value: 'improve', label: 'Improve' },
  { value: 'replace', label: 'Replace' },
]

function typeColor(slug: string | null): string {
  return slug && TYPE_COLORS[slug] ? TYPE_COLORS[slug] : '#6b7280'
}

// ── Component ────────────────────────────────────────────────────────────────

export default function QuestionEvaluationPanel({
  evaluationId,
  readOnly = false,
}: {
  evaluationId: number
  readOnly?: boolean
}) {
  const [bundle, setBundle] = useState<Bundle | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [activeIdx, setActiveIdx] = useState(0)
  const [savingMap, setSavingMap] = useState<Record<number, 'saving' | 'saved' | 'error' | undefined>>({})
  const debounceTimers = useRef<Record<number, ReturnType<typeof setTimeout>>>({})

  const fetchBundle = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)
      const data = await evaluationsApi.getQuestionEvaluationBundle(evaluationId)
      setBundle(data)
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to load questions')
    } finally {
      setLoading(false)
    }
  }, [evaluationId])

  useEffect(() => {
    fetchBundle()
  }, [fetchBundle])

  // Cleanup debounce timers on unmount
  useEffect(() => {
    return () => {
      for (const t of Object.values(debounceTimers.current)) clearTimeout(t)
    }
  }, [])

  // ── Local state mutation helpers ───────────────────────────────────────────

  const applyLocalUpdate = (idx: number, patch: Partial<QuestionResponse>) => {
    setBundle((prev) => {
      if (!prev) return prev
      const items = [...prev.items]
      const it = items[idx]
      if (!it) return prev
      const prevResp = it.response ?? {
        id: 0,
        evaluation_id: evaluationId,
        question_index: idx,
        scores: {},
        note: null,
        recommendation: null,
        completion_status: 'not_started' as const,
      }
      const nextResp = { ...prevResp, ...patch }
      // recompute completion locally (auth source is server; this keeps UI honest)
      const keys = it.categories.map((c) => c.key)
      const filled = keys.length > 0
        ? keys.every((k) => nextResp.scores[k] != null)
        : Object.keys(nextResp.scores).length > 0
      const anyFilled = Object.keys(nextResp.scores).length > 0
      nextResp.completion_status = filled && keys.length > 0
        ? 'completed'
        : anyFilled
          ? 'in_progress'
          : 'not_started'
      items[idx] = { ...it, response: nextResp }
      // recompute bundle counters
      let completed = 0, inProgress = 0
      for (const r of items) {
        if (r.response?.completion_status === 'completed') completed++
        else if (r.response?.completion_status === 'in_progress') inProgress++
      }
      return { ...prev, items, completed, in_progress: inProgress }
    })
  }

  const queueSave = useCallback(
    (idx: number, payload: { scores?: Record<string, number>; note?: string | null; recommendation?: string | null }) => {
      setSavingMap((m) => ({ ...m, [idx]: 'saving' }))
      if (debounceTimers.current[idx]) clearTimeout(debounceTimers.current[idx])
      debounceTimers.current[idx] = setTimeout(async () => {
        try {
          const saved = await evaluationsApi.upsertQuestionResponse(evaluationId, idx, payload)
          setBundle((prev) => {
            if (!prev) return prev
            const items = [...prev.items]
            if (items[idx]) items[idx] = { ...items[idx], response: saved }
            let completed = 0, inProgress = 0
            for (const r of items) {
              if (r.response?.completion_status === 'completed') completed++
              else if (r.response?.completion_status === 'in_progress') inProgress++
            }
            return { ...prev, items, completed, in_progress: inProgress }
          })
          setSavingMap((m) => ({ ...m, [idx]: 'saved' }))
          setTimeout(() => setSavingMap((m) => ({ ...m, [idx]: undefined })), 1500)
        } catch {
          // Surface the failure instead of silently clearing the indicator —
          // the optimistic local edit is NOT persisted, so the interviewer must
          // know. The 'error' state is left in place (no auto-clear); the next
          // field change re-queues a save and flips it back to 'saving'.
          setSavingMap((m) => ({ ...m, [idx]: 'error' }))
        }
      }, 700)
    },
    [evaluationId],
  )

  // ── Handlers ───────────────────────────────────────────────────────────────

  const handleScoreClick = (idx: number, key: string, value: number) => {
    if (readOnly) return
    const item = bundle?.items[idx]
    if (!item) return
    const current = item.response?.scores[key]
    const nextVal = current === value ? undefined : value
    const newScores = { ...(item.response?.scores ?? {}) }
    if (nextVal == null) delete newScores[key]
    else newScores[key] = nextVal
    applyLocalUpdate(idx, { scores: newScores })
    // Server PUT merges; for clearing we'd need to handle removal. For MVP send the full map.
    queueSave(idx, { scores: newScores })
  }

  const handleNoteChange = (idx: number, value: string) => {
    if (readOnly) return
    applyLocalUpdate(idx, { note: value })
    queueSave(idx, { note: value })
  }

  const handleRecommendationChange = (idx: number, value: string) => {
    if (readOnly) return
    applyLocalUpdate(idx, { recommendation: value || null })
    queueSave(idx, { recommendation: value || null })
  }

  // ── Derived ────────────────────────────────────────────────────────────────

  const active = bundle?.items[activeIdx] ?? null
  const progressPct = useMemo(() => {
    if (!bundle || bundle.total === 0) return 0
    return Math.round((bundle.completed / bundle.total) * 100)
  }, [bundle])

  const legendTypes = useMemo(() => {
    if (!bundle) return []
    const seen = new Set<string>()
    const types: { slug: string; name: string }[] = []
    for (const it of bundle.items) {
      if (it.question_type_slug && !seen.has(it.question_type_slug)) {
        seen.add(it.question_type_slug)
        types.push({ slug: it.question_type_slug, name: it.question_type_name ?? it.question_type_slug })
      }
    }
    return types
  }, [bundle])

  // ── Render ─────────────────────────────────────────────────────────────────

  if (loading) {
    return (
      <div className="qep-loading">
        <Loader2 size={18} className="qep-spin" /> Loading questions…
      </div>
    )
  }

  if (error) {
    return (
      <div className="qep-error">
        <AlertCircle size={16} /> {error}
      </div>
    )
  }

  if (!bundle || bundle.total === 0) {
    return (
      <div className="qep-empty">
        <AlertCircle size={20} style={{ color: 'var(--text-muted)', marginBottom: 8 }} />
        <div style={{ fontWeight: 600, color: 'var(--text-dark)', marginBottom: 4 }}>No questions prepared</div>
        <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>
          Prepare a question set for this session to unlock per-question evaluation.
        </div>
        <style>{STYLES}</style>
      </div>
    )
  }

  return (
    <div className="qep-root">
      {/* ── Read-only banner (shown after evaluation is delivered) ─────────── */}
      {readOnly && (
        <div className="qep-locked-banner">
          <Lock size={14} />
          <span>This evaluation has been submitted. Question ratings are view-only and can no longer be changed.</span>
        </div>
      )}

      {/* ── Progress header ────────────────────────────────────────────────── */}
      <div className="qep-progress-head">
        <div className="qep-progress-title">Question Progress</div>
        <div className="qep-progress-count">{bundle.completed}/{bundle.total} completed</div>
      </div>
      <div className="qep-progress-bar">
        <div className="qep-progress-fill" style={{ width: `${progressPct}%` }} />
      </div>
      <div className="qep-progress-pills">
        <span className="qep-pill qep-pill--done"><CheckCircle size={11} /> Completed ({bundle.completed})</span>
        <span className="qep-pill qep-pill--prog"><Clock size={11} /> In Progress ({bundle.in_progress})</span>
        <span className="qep-pill qep-pill--todo"><AlertCircle size={11} /> Not Started ({bundle.total - bundle.completed - bundle.in_progress})</span>
      </div>

      {/* ── Legend ─────────────────────────────────────────────────────────── */}
      {legendTypes.length > 0 && (
        <div className="qep-legend">
          {legendTypes.map((t) => (
            <span key={t.slug} className="qep-legend-item">
              <span className="qep-legend-dot" style={{ background: typeColor(t.slug) }} />
              {t.name}
            </span>
          ))}
        </div>
      )}

      {/* ── Pager ──────────────────────────────────────────────────────────── */}
      <div className="qep-pager">
        {bundle.items.map((it, i) => {
          const status = it.response?.completion_status ?? 'not_started'
          const color = typeColor(it.question_type_slug)
          const isActive = i === activeIdx
          return (
            <button
              key={i}
              type="button"
              className={`qep-page-btn ${isActive ? 'qep-page-btn--active' : ''}`}
              onClick={() => setActiveIdx(i)}
              style={{
                borderColor: isActive ? color : 'var(--border)',
                background: isActive ? color : 'white',
                color: isActive ? 'white' : 'var(--text-dark)',
              }}
            >
              {i + 1}
              <span
                className={`qep-page-dot qep-page-dot--${status}`}
                title={status.replace('_', ' ')}
              />
            </button>
          )
        })}
      </div>

      {/* ── Active question card ───────────────────────────────────────────── */}
      {active && (
        <div className="qep-question-card">
          {/* header */}
          <div className="qep-qhead">
            <div className="qep-qlabel">Q{activeIdx + 1} of {bundle.total}</div>
            {active.question_type_name && (
              <span className="qep-type-badge" style={{ color: typeColor(active.question_type_slug), borderColor: typeColor(active.question_type_slug) }}>
                {active.question_type_name}
              </span>
            )}
            <div className="qep-qhead-spacer" />
            <SaveIndicator state={savingMap[activeIdx]} />
          </div>
          <div className="qep-qtext">{active.question_text}</div>
          {active.expected_answer && (
            <div className="qep-expected">
              <div className="qep-expected-label">Expected Answer</div>
              <p className="qep-expected-body">{active.expected_answer}</p>
            </div>
          )}

          {/* categories */}
          {active.categories.length === 0 ? (
            <div className="qep-nocats">
              No categories are defined for this question type. An admin can add them in the Content page → Question Types → Categories.
            </div>
          ) : (
            <div className="qep-cats">
              {active.categories.map((cat) => {
                const currentVal = active.response?.scores[cat.key]
                return (
                  <div key={cat.id} className="qep-cat-row">
                    <div className="qep-cat-info">
                      <div className="qep-cat-label">{cat.label}</div>
                      <div className="qep-cat-desc">{cat.description}</div>
                    </div>
                    <div className="qep-scale">
                      {[1, 2, 3, 4, 5].map((v) => (
                        <button
                          key={v}
                          type="button"
                          className={`qep-score-btn ${currentVal === v ? 'qep-score-btn--active' : ''}`}
                          onClick={() => handleScoreClick(activeIdx, cat.key, v)}
                          disabled={readOnly}
                          aria-label={`${cat.label} ${v} ${RATING_LABELS[v - 1]}`}
                        >
                          <span className="qep-score-num">{v}</span>
                          <span className="qep-score-lbl">{RATING_LABELS[v - 1]}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                )
              })}
            </div>
          )}

          {/* note + recommendation */}
          <div className="qep-extras">
            <div className="qep-extra">
              <label className="qep-extra-label">Interviewer note (optional)</label>
              <textarea
                className="qep-textarea"
                placeholder="What most affected how this answer landed?"
                rows={2}
                value={active.response?.note ?? ''}
                onChange={(e) => handleNoteChange(activeIdx, e.target.value)}
                disabled={readOnly}
                readOnly={readOnly}
              />
            </div>
            <div className="qep-extra qep-extra--rec">
              <label className="qep-extra-label">Answer recommendation</label>
              <select
                className="qep-select"
                value={active.response?.recommendation ?? ''}
                onChange={(e) => handleRecommendationChange(activeIdx, e.target.value)}
                disabled={readOnly}
              >
                {RECOMMENDATIONS.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </div>
          </div>

          {/* nav */}
          <div className="qep-nav">
            <button
              type="button"
              className="qep-nav-btn"
              disabled={activeIdx === 0}
              onClick={() => setActiveIdx((i) => Math.max(0, i - 1))}
            >
              ← Previous
            </button>
            <button
              type="button"
              className="qep-nav-btn qep-nav-btn--primary"
              disabled={activeIdx >= bundle.total - 1}
              onClick={() => setActiveIdx((i) => Math.min(bundle.total - 1, i + 1))}
            >
              Next →
            </button>
          </div>
        </div>
      )}

      <style>{STYLES}</style>
    </div>
  )
}

// ── Save indicator ─────────────────────────────────────────────────────────

function SaveIndicator({ state }: { state?: 'saving' | 'saved' | 'error' }) {
  if (state === 'saving') return <span className="qep-save qep-save--saving"><Loader2 size={11} className="qep-spin" /> Saving…</span>
  if (state === 'saved')  return <span className="qep-save qep-save--saved"><CheckCircle size={11} /> Saved</span>
  if (state === 'error')  return <span className="qep-save qep-save--error" style={{ color: '#b91c1c' }}><AlertCircle size={11} /> Not saved — change a field to retry</span>
  return null
}

// ── Styles ───────────────────────────────────────────────────────────────────

const STYLES = `
.qep-root { background: white; border: 1px solid var(--border); border-radius: 14px; padding: 20px; box-shadow: var(--shadow); margin-bottom: 20px; }

.qep-loading, .qep-error, .qep-empty { background: white; border: 1px solid var(--border); border-radius: 14px; padding: 20px; text-align: center; margin-bottom: 20px; display: flex; flex-direction: column; align-items: center; gap: 6px; color: var(--text-muted); font-size: 14px; }
.qep-error { color: #b91c1c; background: #fef2f2; border-color: #fecaca; flex-direction: row; justify-content: center; }
.qep-spin { animation: qep-spin 1s linear infinite; }
@keyframes qep-spin { 100% { transform: rotate(360deg); } }

.qep-progress-head { display: flex; justify-content: space-between; align-items: center; margin-bottom: 8px; }
.qep-progress-title { font-size: 14px; font-weight: 600; color: var(--text-dark); }
.qep-progress-count { font-size: 13px; color: var(--text-muted); }
.qep-progress-bar { height: 8px; background: #f3f4f6; border-radius: 4px; overflow: hidden; margin-bottom: 10px; }
.qep-progress-fill { height: 100%; background: linear-gradient(90deg, #16a34a, #22c55e); transition: width 0.3s; }
.qep-progress-pills { display: flex; flex-wrap: wrap; gap: 8px; margin-bottom: 14px; }
.qep-pill { display: inline-flex; align-items: center; gap: 4px; font-size: 11px; font-weight: 600; padding: 3px 9px; border-radius: 20px; }
.qep-pill--done { background: #f0fdf4; color: #15803d; }
.qep-pill--prog { background: #fffbeb; color: #a16207; }
.qep-pill--todo { background: #f3f4f6; color: #4b5563; }

.qep-legend { display: flex; flex-wrap: wrap; gap: 10px; padding: 8px 0; border-top: 1px dashed var(--border); border-bottom: 1px dashed var(--border); margin-bottom: 14px; }
.qep-legend-item { display: inline-flex; align-items: center; gap: 5px; font-size: 11px; color: var(--text-muted); }
.qep-legend-dot { width: 8px; height: 8px; border-radius: 50%; }

.qep-pager { display: flex; gap: 6px; flex-wrap: wrap; margin-bottom: 16px; }
.qep-page-btn { position: relative; width: 36px; height: 36px; border-radius: 8px; border: 1.5px solid var(--border); background: white; font-weight: 600; font-size: 13px; cursor: pointer; transition: all 0.15s; }
.qep-page-btn:hover:not(.qep-page-btn--active) { background: #f9fafb; }
.qep-page-dot { position: absolute; top: 2px; right: 2px; width: 7px; height: 7px; border-radius: 50%; background: #d1d5db; }
.qep-page-dot--in_progress { background: #f59e0b; }
.qep-page-dot--completed { background: #16a34a; }

.qep-question-card { border-top: 1px solid var(--border); padding-top: 16px; }
.qep-qhead { display: flex; align-items: center; gap: 10px; margin-bottom: 10px; flex-wrap: wrap; }
.qep-qlabel { font-size: 11px; font-weight: 700; color: var(--text-muted); letter-spacing: 0.5px; text-transform: uppercase; }
.qep-qhead-spacer { flex: 1; }
.qep-type-badge { display: inline-flex; align-items: center; gap: 4px; font-size: 11px; font-weight: 600; padding: 2px 9px; border: 1.5px solid; border-radius: 14px; background: white; }
.qep-qtext { font-size: 15px; font-weight: 500; color: var(--text-dark); padding: 12px 14px; background: #f9fafb; border-left: 3px solid var(--primary-navy); border-radius: 6px; margin-bottom: 16px; line-height: 1.5; }

.qep-expected { padding: 10px 14px; background: rgba(139,92,246,0.06); border-left: 3px solid #7c3aed; border-radius: 6px; margin: -8px 0 16px; }
.qep-expected-label { font-size: 10px; font-weight: 700; color: #7c3aed; text-transform: uppercase; letter-spacing: 0.5px; margin-bottom: 4px; }
.qep-expected-body { font-size: 12px; color: var(--text-dark); line-height: 1.6; margin: 0; white-space: pre-wrap; }

.qep-nocats { font-size: 13px; color: var(--text-muted); padding: 20px; background: #fffbeb; border: 1px dashed #fde047; border-radius: 8px; text-align: center; margin-bottom: 14px; }

.qep-cats { display: flex; flex-direction: column; gap: 10px; margin-bottom: 16px; }
.qep-cat-row { display: flex; gap: 12px; align-items: center; padding: 12px 14px; border: 1px solid var(--border); border-radius: 10px; flex-wrap: wrap; }
.qep-cat-info { flex: 1; min-width: 240px; }
.qep-cat-label { font-size: 13px; font-weight: 600; color: var(--text-dark); margin-bottom: 2px; }
.qep-cat-desc { font-size: 12px; color: var(--text-muted); line-height: 1.4; }

.qep-scale { display: flex; gap: 4px; flex-shrink: 0; }
.qep-score-btn { display: flex; flex-direction: column; align-items: center; justify-content: center; min-width: 56px; padding: 6px 8px; border: 1.5px solid var(--border); background: white; border-radius: 8px; cursor: pointer; transition: all 0.12s; }
.qep-score-btn:hover:not(.qep-score-btn--active) { background: #f3f4f6; border-color: #c7d2fe; }
.qep-score-btn--active { background: var(--primary-navy); border-color: var(--primary-navy); color: white; }
.qep-score-btn:disabled { cursor: not-allowed; opacity: 0.85; }
.qep-score-btn:disabled:hover:not(.qep-score-btn--active) { background: white; border-color: var(--border); }
.qep-textarea:disabled, .qep-select:disabled { background: #f9fafb; cursor: not-allowed; color: var(--text-dark); opacity: 0.9; }

.qep-locked-banner { display: flex; align-items: center; gap: 8px; padding: 10px 14px; background: #fef9c3; border: 1px solid #fde047; color: #854d0e; border-radius: 8px; font-size: 13px; margin-bottom: 14px; }
.qep-score-num { font-size: 14px; font-weight: 700; line-height: 1; }
.qep-score-lbl { font-size: 8px; font-weight: 600; letter-spacing: 0.3px; text-transform: uppercase; margin-top: 3px; opacity: 0.85; }

.qep-extras { display: grid; grid-template-columns: 2fr 1fr; gap: 12px; margin-bottom: 12px; }
.qep-extra-label { display: block; font-size: 11px; font-weight: 600; color: var(--text-muted); margin-bottom: 4px; text-transform: uppercase; letter-spacing: 0.3px; }
.qep-textarea { width: 100%; font-family: inherit; font-size: 13px; padding: 8px 10px; border: 1px solid var(--border); border-radius: 8px; resize: vertical; }
.qep-textarea:focus, .qep-select:focus { outline: 2px solid #c7d2fe; outline-offset: -1px; border-color: var(--primary-navy); }
.qep-select { width: 100%; font-size: 13px; padding: 8px 10px; border: 1px solid var(--border); border-radius: 8px; background: white; }

.qep-nav { display: flex; justify-content: space-between; gap: 8px; margin-top: 10px; border-top: 1px dashed var(--border); padding-top: 12px; }
.qep-nav-btn { padding: 8px 16px; border: 1px solid var(--border); background: white; border-radius: 8px; cursor: pointer; font-size: 13px; font-weight: 500; color: var(--text-dark); }
.qep-nav-btn:hover:not(:disabled) { background: #f3f4f6; }
.qep-nav-btn:disabled { opacity: 0.45; cursor: not-allowed; }
.qep-nav-btn--primary { background: var(--primary-navy); color: white; border-color: var(--primary-navy); }
.qep-nav-btn--primary:hover:not(:disabled) { background: #111827; }

.qep-save { display: inline-flex; align-items: center; gap: 4px; font-size: 11px; font-weight: 600; padding: 3px 8px; border-radius: 6px; }
.qep-save--saving { background: #fffbeb; color: #a16207; }
.qep-save--saved  { background: #f0fdf4; color: #15803d; }

@media (max-width: 640px) {
  .qep-extras { grid-template-columns: 1fr; }
  .qep-scale { width: 100%; justify-content: space-between; }
  .qep-score-btn { min-width: 0; flex: 1; }
}
`
